import {
  ListFieldList,
  LogicFieldList,
  TextFieldList,
} from '@/entities/Inventory/TypeField/model/types'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import type { RuntimeFieldValueMap } from '@/features/Inventory/ObjectListEdit/model/runtimeFieldBindings'
import { parseLeafFieldIdForLinkedResolution } from './passportRefGridLinkedParse'
import { isElementLinkedFieldCell } from '@/features/Inventory/ObjectTypePassportGrid/model/passportFieldPalette'
import {
  parsePrefixedPaletteFieldId,
  resolveRuntimeFieldMetadataForGridItem,
  type RuntimeFieldSlot,
  type RuntimePlaceholderItem,
} from './runtimeLayoutAdapter'
import { isListCatalogNameGridId } from './listCatalogNameGrid'
import { isParentObjectNameGridId } from './parentObjectNameGrid'

/** Same empty/trim logic as all-fields drawer read-only rows. */
export function displayFieldValueString(
  fieldValues: RuntimeFieldValueMap,
  fieldId: string,
  trs: (k: string) => string,
): string {
  const raw = fieldValues[fieldId]
  if (raw === undefined || raw === null || raw === '') {
    return trs('itf.not_set')
  }
  return String(raw)
}

/**
 * Pill text for parent / linked cells: composite `ref:…#f:` paths resolve via `linkedByGridId` (any field type).
 * `list_types` also uses linked labels; own leaf values come from `fieldValues` when not a `ref:` id.
 */
export function parentPillTextForSlot(
  item: RuntimeFieldSlot,
  fieldValues: RuntimeFieldValueMap,
  linkedByGridId: Record<string, string>,
  linkedLoading: boolean,
  trs: (k: string) => string,
): string {
  const gid = String(item.layoutItem.id)
  const linked = linkedByGridId[gid]
  const fieldType = String(item.field.type ?? '')
  const isRefComposite = gid.startsWith('ref:')

  if (isRefComposite) {
    if (linkedLoading) return trs('itf.not_set')
    if (linked !== undefined) {
      const v = String(linked)
      return v === '' ? '-' : v
    }
    return '-'
  }

  if (isElementLinkedFieldCell(item.layoutItem)) {
    if (linkedLoading) return trs('itf.not_set')
    if (linked !== undefined) {
      const v = String(linked)
      return v === '' ? '-' : v
    }
    return '-'
  }

  if (fieldType === ListFieldList.LIST_TYPES) {
    if (linkedLoading) return trs('itf.not_set')
    if (linked !== undefined && linked !== '') return String(linked)
    return trs('itf.not_set')
  }

  if (isParentObjectNameGridId(gid) || isListCatalogNameGridId(gid)) {
    if (linkedLoading) return trs('itf.not_set')
    if (linked !== undefined) {
      const v = String(linked)
      return v === '' ? '-' : v
    }
    return '-'
  }

  return displayFieldValueString(fieldValues, item.fieldId, trs)
}

/** Clipboard: for `ref:` composite cells use resolved linked text when present; else leaf on root object. */
export function parentRawCopyValue(
  item: RuntimeFieldSlot,
  fieldValues: RuntimeFieldValueMap,
  linkedByGridId?: Record<string, string>,
): string {
  const gid = String(item.layoutItem.id)
  if (gid.startsWith('ref:') && linkedByGridId) {
    const linked = linkedByGridId[gid]
    if (linked !== undefined && linked !== '') return String(linked)
  }
  if (linkedByGridId && isElementLinkedFieldCell(item.layoutItem)) {
    const linked = linkedByGridId[gid]
    if (linked !== undefined && linked !== '') return String(linked)
    return ''
  }
  if (
    linkedByGridId &&
    (isParentObjectNameGridId(gid) || isListCatalogNameGridId(gid))
  ) {
    const linked = linkedByGridId[gid]
    if (linked !== undefined && linked !== '') return String(linked)
    return ''
  }
  const raw = fieldValues[item.fieldId]
  if (raw === undefined || raw === null) return ''
  return String(raw)
}

export function getFieldMetaForPlaceholder(
  item: RuntimePlaceholderItem,
  allFields: ObjectInventoryField[],
): ObjectInventoryField | undefined {
  const resolved = resolveRuntimeFieldMetadataForGridItem(item.layoutItem, allFields)
  if (resolved) return resolved

  const li = item.layoutItem as GridFieldType & { inventory_field_id?: string | number }
  if (li.inventory_field_id != null) {
    const byInv = allFields.find((f) => String(f.id) === String(li.inventory_field_id))
    if (byInv) return byInv
  }
  const direct = allFields.find((f) => String(f.id) === String(li.id))
  if (direct) return direct

  const prefixed = parsePrefixedPaletteFieldId(String(li.id))
  if (prefixed) {
    return allFields.find((f) => String(f.id) === prefixed)
  }
  return undefined
}

export function focusFieldIdForPlaceholder(
  item: RuntimePlaceholderItem,
  allFields: ObjectInventoryField[],
): string | null {
  const meta = getFieldMetaForPlaceholder(item, allFields)
  if (meta) return String(meta.id)
  const leaf = parseLeafFieldIdForLinkedResolution(item.layoutItem, undefined)
  if (leaf != null) return String(leaf)
  const prefixed = parsePrefixedPaletteFieldId(String(item.layoutItem.id))
  if (prefixed) return prefixed
  return null
}

export function rawValueForPlaceholderCopy(
  item: RuntimePlaceholderItem,
  allFields: ObjectInventoryField[],
  fieldValues: RuntimeFieldValueMap,
): string {
  const id = focusFieldIdForPlaceholder(item, allFields)
  if (!id) return ''
  const v = fieldValues[id]
  if (v !== undefined && v !== null) return String(v)
  return ''
}

/** Leaf inventory field for `ref:…#f:…` placeholders (by parsed `#f:` id). */
function leafFieldMetaForRefPlaceholder(
  item: RuntimePlaceholderItem,
  allFields: ObjectInventoryField[],
): ObjectInventoryField | undefined {
  const gid = String(item.layoutItem.id)
  if (!gid.startsWith('ref:')) return undefined
  const leafId = parseLeafFieldIdForLinkedResolution(item.layoutItem, undefined)
  if (leafId == null) return undefined
  return allFields.find((f) => String(f.id) === String(leafId))
}

/**
 * Parses API/runtime values that may be string or boolean into a switch model.
 * Linked display is often `"true"` / `"false"` strings even when the catalog marks the leaf as `string`.
 */
export function parseBooleanLikeDisplayValue(raw: unknown): boolean | null {
  if (raw === undefined || raw === null) return null
  if (typeof raw === 'boolean') return raw
  const s = String(raw).trim().toLowerCase()
  if (s === '' || s === '-') return null
  if (s === 'true' || s === '1') return true
  if (s === 'false' || s === '0') return false
  return null
}

/**
 * When a composite `ref:` cell resolves to a boolean-like value, return the value for a read-only switch; otherwise `null` (use plain text).
 * `linkedByGridId` values may be strings, booleans, or other primitives from the resolver/runtime.
 */
export function unsupportedRefBooleanSwitchValue(
  item: RuntimePlaceholderItem,
  allFields: ObjectInventoryField[],
  linkedByGridId: Record<string, unknown>,
  linkedLoading: boolean,
  /** Create-batch draft: use leaf `fieldValues` when linked map has no HTTP-resolved value. */
  fieldValues?: RuntimeFieldValueMap,
): boolean | null {
  const gid = String(item.layoutItem.id)
  if (!gid.startsWith('ref:') || linkedLoading) {
    return null
  }
  const meta = leafFieldMetaForRefPlaceholder(item, allFields)
  let linked: unknown = linkedByGridId[gid]
  if (linked === undefined && fieldValues) {
    const leafId = focusFieldIdForPlaceholder(item, allFields)
    if (leafId) {
      const raw = fieldValues[leafId]
      if (raw !== undefined && raw !== null) linked = raw
    }
  }
  const parsed = linked === undefined ? null : parseBooleanLikeDisplayValue(linked)
  let result: boolean | null = null
  if (!meta) {
    /** Leaf ids from linked types (e.g. UUID) are often absent from the current catalog `allFields`. */
    if (parsed !== null) {
      result = parsed
    }
  } else {
    const t = String(meta.type)
    const boolLeaf = t === LogicFieldList.BOOLEAN
    const stringLeaf = t === TextFieldList.STRING || t === 'string'
    if (boolLeaf || stringLeaf) {
      if (linked !== undefined && parsed !== null) {
        result = parsed
      }
    }
  }
  return result
}

/**
 * Plain text the unsupported cell would have shown if the widget were implemented (drawer-consistent slices).
 */
export function unsupportedAttemptedDisplay(
  item: RuntimePlaceholderItem,
  fieldValues: RuntimeFieldValueMap,
  linkedByGridId: Record<string, string>,
  linkedLoading: boolean,
  allFields: ObjectInventoryField[],
  trs: (k: string) => string,
): string {
  const gid = String(item.layoutItem.id)
  const linked = linkedByGridId[gid]
  const isRefComposite = gid.startsWith('ref:')
  const meta = getFieldMetaForPlaceholder(item, allFields)
  const fieldId = focusFieldIdForPlaceholder(item, allFields)
  const raw = fieldId ? fieldValues[fieldId] : undefined

  if (isRefComposite) {
    if (linkedLoading) return trs('itf.not_set')
    if (linked !== undefined) {
      const v = String(linked)
      return v === '' ? '-' : v
    }
    /** Draft / create-batch: no HTTP linked map yet — show leaf `fieldValues` (same as drawer). */
    if (raw !== undefined && raw !== null && String(raw).trim() !== '') return String(raw)
    return '-'
  }

  if (meta && String(meta.type) === ListFieldList.LIST_TYPES) {
    if (linkedLoading) return trs('itf.not_set')
    if (linked !== undefined && linked !== '') return String(linked)
    if (raw !== undefined && raw !== null && raw !== '') return String(raw)
    return trs('itf.passport_runtime_value_not_specified')
  }

  if (raw !== undefined && raw !== null && raw !== '') return String(raw)
  if (linked !== undefined && linked !== '') return String(linked)
  return trs('itf.passport_runtime_value_not_specified')
}

/**
 * Hover tooltip on truncated passport value: only parent-palette string fields when the visible text is long.
 */
export function shouldShowPassportRuntimeValueTooltip(input: {
  fieldType: string | undefined | null
  layoutPaletteSource: string | undefined | null
  displayText: string
}): boolean {
  if (String(input.fieldType ?? '') !== TextFieldList.STRING) return false
  if (String(input.layoutPaletteSource ?? '') !== 'parent') return false
  return String(input.displayText ?? '').trim().length > 6
}
